import { Search } from "@mui/icons-material"
import { Autocomplete, FilterOptionsState } from "@mui/material"
import React, { ChangeEvent, useState } from "react"
import styled from "styled-components"
import { theme } from "../../../theme"
import { TextField } from "../../atoms/inputs"

export type HelpQuestionOption = {
  title: string
  category: string
}

export type HelpCenterAutocompleteProps<TOption> = {
  noResultsMessage: string
  label: string
  options: TOption[]
  helperText?: string
  filterOptions?: (ops: TOption[], inputState: FilterOptionsState<TOption>) => TOption[]
  onSelect: (option: TOption | null) => void
}

export const HelpCenterAutocomplete = <TOption extends HelpQuestionOption>(
  props: HelpCenterAutocompleteProps<TOption>
) => {
  const { options, label, helperText, filterOptions, noResultsMessage, onSelect } = props

  const [open, setOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [autocompleteValue, setAutocompleteValue] = useState<TOption | null>(null)

  const shouldShrinkLabel = isFocused || searchValue.length > 0

  const handleFocus = () => {
    setOpen(true)
    setIsFocused(true)
  }

  const handleBlur = () => {
    setOpen(false)
    setIsFocused(false)
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setOpen(true)
    setSearchValue(e.target.value)
  }

  const handleAutocompleteChange = (_e: any, value: TOption | null) => {
    onSelect(value)
    setAutocompleteValue(value)
    setOpen(false)
    setSearchValue(value?.title ?? "")
  }

  const escapeRegExp = (value: string) => {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }

  const getHighlightedContent = (content: string) => {
    if (searchValue.length === 0) return content

    const parts = content.split(new RegExp(`(${escapeRegExp(searchValue)})`, "gi"))
    return parts.map((part, i) => {
      if (part.toLowerCase() === searchValue.toLowerCase()) {
        return <Highlighted key={i}>{part}</Highlighted>
      }

      return <span key={i}>{part}</span>
    })
  }

  return (
    <Root
      open={open}
      value={autocompleteValue}
      options={options}
      filterOptions={filterOptions}
      noOptionsText={noResultsMessage}
      getOptionLabel={(option) => option.title}
      onChange={handleAutocompleteChange}
      renderOption={(renderProps, option) => (
        <li key={option.title} {...renderProps}>
          <OptionWrapper>
            <Title>{getHighlightedContent(option.title)}</Title>
            <Path>
              <span>{getHighlightedContent(option.category)}</span>
            </Path>
          </OptionWrapper>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          value={searchValue}
          label={label}
          helperText={helperText}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          InputLabelProps={{
            shrink: shouldShrinkLabel,
          }}
          startAdornment={<SearchIcon />}
        />
      )}
    />
  )
}

const Root = styled(Autocomplete)`
  .MuiInputLabel-root[data-shrink="false"] {
    margin-left: ${theme.spacing.xl};
  }
` as typeof Autocomplete

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const Title = styled.div`
  font-size: 16px;
  line-height: ${theme.lineHeight.m};
  color: ${theme.color.black6};
`

const Path = styled.div`
  font-size: ${theme.fontSize.s};
  color: ${theme.color.black4};
`

const Highlighted = styled.span`
  font-family: ${theme.font.bold};
  color: ${theme.color.black6};
`

const SearchIcon = styled(Search)`
  color: ${theme.color.grey2};
  margin-right: ${theme.spacing.s};
`
